// components/GlobalSearch/GlobalSearch.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Loader2, User, ShoppingCart, RefreshCw, RotateCcw, Truck, Package, ArrowRight, Phone, Mail, ClipboardList, CheckCircle, ArrowLeft, ExternalLink } from 'lucide-react';
import { cn } from '@/utils/cn';
import { SearchResultItem, SearchResults, SearchCategory, LeadDetailData, OrderSummaryData, OrderCardData } from '@/types/globalSearch';
import api from '@/utils/api';
import { format, parseISO, isToday, isPast } from 'date-fns';
import { CLAIM_REASONS, claimsService } from '@/services/claimsService';
import { showToast } from '@/components/ui/Toast';

interface GlobalSearchProps {
  className?: string;
  variant?: 'icon' | 'bar';
}

const DEBOUNCE_MS = 450;
const MIN_QUERY_LENGTH = 3;
const EMPTY_RESULTS: SearchResults = { leads: [], customer: [], reorder: [], rto: [], edd: [], orders: [], coupon: null, total: 0 };
const SEARCH_HINT = 'Search customer by Name / Phone / Order ID / AWB / Agent Coupon';
const SEARCH_EXAMPLES = ['Rahul Sharma', '8179790023', 'ZH425376', '123456789012', 'NANO5'];

const categoryConfig: Record<SearchCategory, { label: string; icon: React.ReactNode; color: string }> = {
  leads: { label: 'ABC Leads', icon: <ShoppingCart size={16} />, color: 'bg-blue-100 text-blue-700' },
  customer: { label: 'Customers (order in progress)', icon: <Package size={16} />, color: 'bg-teal-100 text-teal-700' },
  reorder: { label: 'Reorder Leads', icon: <RefreshCw size={16} />, color: 'bg-green-100 text-green-700' },
  rto: { label: 'RTO Leads', icon: <RotateCcw size={16} />, color: 'bg-red-100 text-red-700' },
  edd: { label: 'EDD / NDR Leads', icon: <Truck size={16} />, color: 'bg-amber-100 text-amber-700' },
  orders: { label: 'Orders', icon: <ShoppingCart size={16} />, color: 'bg-purple-100 text-purple-700' },
  coupon: { label: 'Agent Coupon Orders', icon: <ShoppingCart size={16} />, color: 'bg-indigo-100 text-indigo-700' },
  users: { label: 'Users', icon: <User size={16} />, color: 'bg-orange-100 text-orange-700' },
};


const GlobalSearch: React.FC<GlobalSearchProps> = ({ className, variant = 'icon' }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [orderCard, setOrderCard] = useState<OrderCardData | null>(null);
  const [orderLoading, setOrderLoading] = useState(false);

  // Claim state: uuid -> 'idle' | 'claiming' | 'reason' | 'done'
  const [claimState, setClaimState] = useState<Record<string, 'idle' | 'claiming' | 'reason' | 'done'>>({});
  const [claimReason, setClaimReason] = useState<Record<string, string>>({});
  const [claimCode, setClaimCode] = useState<Record<string, string>>({});

  const handleClaim = async (leadUuid: string, reasonCode: string | undefined, note?: string) => {
    if (!reasonCode) {
      showToast('Pick a reason for the claim', 'warning');
      return;
    }
    if (reasonCode === 'other' && !(note || '').trim()) {
      showToast('Say why you need this customer', 'warning');
      return;
    }
    setClaimState(prev => ({ ...prev, [leadUuid]: 'claiming' }));
    try {
      const res: any = await claimsService.submitClaim(leadUuid, reasonCode, note);
      if (res.success) {
        showToast('Claim request submitted successfully', 'success');
        setClaimState(prev => ({ ...prev, [leadUuid]: 'done' }));
      } else {
        showToast(res.message || 'Failed to submit claim', 'error');
        setClaimState(prev => ({ ...prev, [leadUuid]: 'idle' }));
      }
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to submit claim';
      showToast(msg, 'error');
      setClaimState(prev => ({ ...prev, [leadUuid]: 'idle' }));
    }
  };

  // Order the category sections are rendered in — also drives keyboard nav
  // indexes, so both must read from this single list.
  const CATEGORY_ORDER: SearchCategory[] = ['orders', 'coupon', 'leads', 'customer', 'reorder', 'rto', 'edd'];

  const categoryItems = React.useCallback(
    (category: SearchCategory): SearchResultItem[] => {
      if (category === 'coupon') return results?.coupon?.items ?? [];
      return (results?.[category as keyof SearchResults] as SearchResultItem[] | undefined) ?? [];
    },
    [results]
  );

  // Get flattened results for keyboard navigation
  const flatResults = React.useMemo(() => {
    if (!results) return [];
    return CATEGORY_ORDER.flatMap(c => categoryItems(c));
  }, [results, categoryItems]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsOpen(true);
      }
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Search function — the server works out whether this is a name, phone,
  // order id, AWB or agent coupon.
  const performSearch = useCallback(async (text: string) => {
    const q = text.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setResults(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post<SearchResults>('/global-search', { query: q, limit: 10 });

      if (response.success && response.data) {
        setResults(response.data);
      } else {
        setError('No results found');
        setResults(EMPTY_RESULTS);
      }
    } catch (err: any) {
      // 400 = the text matches no searchable shape yet (e.g. "12"): show "no results".
      if (err?.response?.status === 400) {
        setResults(EMPTY_RESULTS);
      } else {
        console.error('Search error:', err);
        setError('Search failed. Please try again.');
        setResults(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search; Enter with nothing highlighted searches immediately.
  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    setSelectedIndex(-1);
    setOrderCard(null);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.trim().length >= MIN_QUERY_LENGTH) {
      setIsLoading(true);
      debounceRef.current = setTimeout(() => {
        performSearch(value);
      }, DEBOUNCE_MS);
    } else {
      setResults(null);
      setIsLoading(false);
    }
  }, [performSearch]);

  const openOrderCard = async (orderUuid: string) => {
    setOrderLoading(true);
    try {
      const response = await api.get<OrderCardData>(`/global-search/order/${orderUuid}`);
      if (response.success && response.data) {
        setOrderCard(response.data);
      } else {
        showToast('Could not load this order', 'error');
      }
    } catch {
      showToast('Could not load this order', 'error');
    } finally {
      setOrderLoading(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && selectedIndex < 0 && query.trim().length >= MIN_QUERY_LENGTH) {
      event.preventDefault();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      performSearch(query);
      return;
    }
    if (flatResults.length === 0 || orderCard) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : flatResults.length - 1));
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && flatResults[selectedIndex]) {
          handleResultClick(flatResults[selectedIndex]);
        }
        break;
    }
  };

  // Handle result click — open lead detail page in a new tab
  const handleResultClick = (result: SearchResultItem) => {
    // Order / AWB / coupon hits open the read-only order card inside the modal.
    if (result.type === 'orders' || result.type === 'coupon') {
      openOrderCard(result.uuid);
      return;
    }
    const data = result.data as LeadDetailData | undefined;
    if (!data?.uuid) return;

    handleClose();
    window.open(`/lead/${data.uuid}`, '_blank');
  };

  // Close modal and reset state
  const handleClose = () => {
    setIsOpen(false);
    setQuery('');
    setResults(null);
    setError(null);
    setSelectedIndex(-1);
    setOrderCard(null);
  };

  // Format date helper
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return null;
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return 'Today';
      return format(date, 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  // Format currency

  const money = (v?: string | null) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);

  // Compact order row (Order ID / AWB / phone / coupon hits)
  const renderOrderRow = (item: SearchResultItem, index: number) => {
    const isSelected = index === selectedIndex;
    const config = categoryConfig[item.type];
    const d = item.data as OrderSummaryData | undefined;
    return (
      <button
        key={`${item.type}-${item.uuid}`}
        onClick={() => handleResultClick(item)}
        onMouseEnter={() => setSelectedIndex(index)}
        className={cn(
          'w-full flex items-center gap-4 px-6 py-3 text-left border-b border-gray-100 transition-colors',
          isSelected ? 'bg-brandColor/5' : 'hover:bg-gray-50'
        )}
      >
        <div className={cn('flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0', config.color)}>
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{item.title}</span>
            {d?.delivery_status && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">{d.delivery_status}</span>
            )}
            {d?.financial_status && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{d.financial_status}</span>
            )}
          </div>
          <div className="text-sm text-gray-500 mt-0.5 truncate">
            {item.subtitle}
            {d?.order_date && <span className="text-gray-400"> • {formatDate(d.order_date)}</span>}
          </div>
        </div>
        <span className="text-sm font-medium text-gray-700 flex-shrink-0">{money(d?.total_price)}</span>
        <ArrowRight size={18} className="text-gray-400 flex-shrink-0" />
      </button>
    );
  };

  // Read-only order card: what agents used to open Shopify for.
  const renderOrderCard = (o: OrderCardData) => {
    const addr = o.shipping_address || {};
    const addrLine = [addr.name, addr.address1, addr.address2, addr.city, addr.province, addr.zip, addr.phone]
      .filter(Boolean).join(', ');
    const sh = o.shipment;
    const row = (label: string, value?: string | null) => (
      <div className="flex gap-3 text-sm py-0.5">
        <span className="w-36 flex-shrink-0 text-gray-500">{label}</span>
        <span className="text-gray-900 break-words min-w-0">{value || '—'}</span>
      </div>
    );
    const section = (title: string, children: React.ReactNode) => (
      <div className="px-6 py-4 border-b border-gray-100">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{title}</h4>
        {children}
      </div>
    );
    return (
      <div>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
          <button onClick={() => setOrderCard(null)} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
            <ArrowLeft size={16} /> Back
          </button>
          <span className="text-xl font-semibold text-gray-900">{o.order_name}</span>
          {sh.status && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">{sh.status}</span>}
          {o.financial_status && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{o.financial_status}</span>}
          {o.cancelled_at && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Cancelled</span>}
          <span className="text-sm text-gray-400 ml-auto">{o.order_date ? formatDate(o.order_date) : ''}</span>
        </div>

        {(o.lead || o.work_items.length > 0) && (
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2 flex-wrap text-sm">
            <span className="text-gray-500">Open in CRM:</span>
            {o.lead && (
              <button onClick={() => window.open(o.lead!.url, '_blank')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-white">
                <ExternalLink size={13} /> Lead{o.lead.owner_name ? ` (${o.lead.owner_name})` : ''}
              </button>
            )}
            {o.work_items.map(w => (
              <button key={w.type} onClick={() => window.open(w.url, '_blank')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-brandColor text-brandColor hover:bg-brandColor hover:text-white">
                <ExternalLink size={13} /> {w.type.toUpperCase()} work item
              </button>
            ))}
          </div>
        )}

        {section('Customer', <>
          {row('Name', o.customer_name)}
          {row('Phone', o.phone)}
          {row('Email', o.email)}
          {row('Ship to', addrLine)}
        </>)}

        {section('Shipment', <>
          {row('AWB', sh.awb)}
          {row('Courier', sh.courier)}
          {row('Status', [sh.status, sh.location].filter(Boolean).join(' — '))}
          {row('Last update', sh.event_at ? formatDate(sh.event_at) : null)}
          {row('Expected delivery', sh.expected_delivery ? formatDate(sh.expected_delivery) : null)}
          {row('Delivered on', sh.delivered_at ? formatDate(sh.delivered_at) : null)}
          {o.ndr && o.ndr.attempts.length > 0 && (
            <div className="mt-2 text-sm">
              <div className="text-gray-500 mb-1">Failed delivery attempts ({o.ndr.total})</div>
              {o.ndr.attempts.map((a, i) => (
                <div key={i} className="text-gray-800">
                  {a.attempt_date ? formatDate(a.attempt_date) : '—'}: {a.reason || 'No reason given'}
                </div>
              ))}
            </div>
          )}
        </>)}

        {section('Items', <>
          {o.items.map((it, i) => (
            <div key={i} className="flex justify-between gap-3 text-sm py-0.5">
              <span className="text-gray-900">{it.title}{it.variant_title ? ` — ${it.variant_title}` : ''} <span className="text-gray-400">× {it.quantity}</span></span>
              <span className="text-gray-700 flex-shrink-0">{money(it.total_price)}</span>
            </div>
          ))}
        </>)}

        {section('Payment', <>
          {row('Payment mode', o.payment_gateway)}
          {row('Subtotal', money(o.subtotal))}
          {row('Discounts', money(o.discounts))}
          {row('Shipping', money(o.shipping))}
          {row('Total', money(o.total))}
          {row('Discount codes', o.discount_codes.join(', '))}
          {row('Credited to', [o.credit.agent, o.credit.assist ? `(assist: ${o.credit.assist})` : ''].filter(Boolean).join(' '))}
        </>)}

        {(o.note || o.tags) && section('Notes', <>
          {row('Tags', o.tags)}
          {row('Note', o.note)}
        </>)}
      </div>
    );
  };

  // Render result item with comprehensive details
  const renderResultItem = (item: SearchResultItem, index: number) => {
    const isSelected = index === selectedIndex;
    const config = categoryConfig[item.type];
    const data = item.data as LeadDetailData | undefined;

    // Check if reorder is due/overdue
    const isReorderDue = data?.next_reorder_date && (isToday(parseISO(data.next_reorder_date)) || isPast(parseISO(data.next_reorder_date)));

    return (
      <div
        key={`${item.type}-${item.uuid}`}
        className={cn(
          'border-b border-gray-100 last:border-b-0 transition-colors',
          isSelected ? 'bg-brandColor/5' : 'hover:bg-gray-50'
        )}
        onMouseEnter={() => setSelectedIndex(index)}
      >
        {/* Main clickable header */}
        <button
          onClick={() => handleResultClick(item)}
          className="w-full flex items-start gap-4 px-6 py-4 text-left"
        >
          {/* Category Icon */}
          <div className={cn('flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 mt-1', config.color)}>
            {config.icon}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Row 1: Name, Badges, Status */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-lg">{item.title || 'Unknown'}</span>
              {isReorderDue && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">
                  Due {formatDate(data?.next_reorder_date)}
                </span>
              )}
            </div>

            {/* Row 2: Phone, Email, Source */}
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
              {data?.phone && (
                <span className="flex items-center gap-1">
                  <Phone size={14} className="text-gray-400" />
                  {data.phone}
                </span>
              )}
              {data?.email && (
                <span className="flex items-center gap-1 truncate">
                  <Mail size={14} className="text-gray-400" />
                  {data.email}
                </span>
              )}
              {data?.source_display && (
                <span className="text-gray-400">• {data.source_display}</span>
              )}
            </div>

            {/* Row 3: Assignment & Disposition */}
            <div className="flex items-center gap-4 mt-2 text-sm">
              {data?.assigned_to ? (
                <span className="flex items-center gap-1 text-blue-600">
                  <User size={14} />
                  {data.assigned_to.user_name}
                  {data.assigned_to.assignment_type === 'reorder' && (
                    <span className="text-xs text-gray-400">(reorder team)</span>
                  )}
                </span>
              ) : (
                <span className="text-gray-400">Unassigned</span>
              )}
              {(data?.disposition_l1_display || data?.disposition_l2_display) && (
                <span className="text-gray-500">
                  {[data.disposition_l1_display, data.disposition_l2_display, data.disposition_l3_display]
                    .filter(Boolean)
                    .join(' → ')}
                </span>
              )}
            </div>


          </div>

          {/* Arrow indicator */}
          <ArrowRight
            size={20}
            className={cn(
              'text-gray-400 flex-shrink-0 mt-2 transition-opacity',
              isSelected ? 'opacity-100' : 'opacity-0'
            )}
          />
        </button>

        {/* Claim section — outside the main button */}
        {item.uuid && (
          <div className="px-6 pb-3 flex items-start gap-2">
            {claimState[item.uuid] === 'done' ? (
              <span className="flex items-center gap-1.5 text-xs text-green-600 font-medium">
                <CheckCircle className="h-3.5 w-3.5" /> Claim request sent
              </span>
            ) : claimState[item.uuid] === 'reason' ? (
              <div className="flex items-center gap-2 w-full flex-wrap">
                <select
                  value={claimCode[item.uuid] || ''}
                  onChange={e => setClaimCode(prev => ({ ...prev, [item.uuid]: e.target.value }))}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brandColor"
                  autoFocus
                >
                  <option value="">Why do you need this customer?</option>
                  {CLAIM_REASONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                </select>
                <input
                  type="text"
                  value={claimReason[item.uuid] || ''}
                  onChange={e => setClaimReason(prev => ({ ...prev, [item.uuid]: e.target.value }))}
                  placeholder={claimCode[item.uuid] === 'other' ? 'Say why (required)' : 'Note (optional)'}
                  className="flex-1 min-w-[140px] px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brandColor"
                />
                <button
                  onClick={() => handleClaim(item.uuid, claimCode[item.uuid], claimReason[item.uuid])}
                  className="px-3 py-1.5 bg-brandColor text-white text-xs font-medium rounded-lg hover:opacity-90 transition-opacity"
                >
                  Submit
                </button>
                <button
                  onClick={() => setClaimState(prev => ({ ...prev, [item.uuid]: 'idle' }))}
                  className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={e => {
                  e.stopPropagation();
                  setClaimState(prev => ({ ...prev, [item.uuid]: 'reason' }));
                }}
                disabled={claimState[item.uuid] === 'claiming'}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brandColor border border-brandColor rounded-lg hover:bg-brandColor hover:text-white transition-colors disabled:opacity-50"
              >
                <ClipboardList className="h-3.5 w-3.5" />
                {claimState[item.uuid] === 'claiming' ? 'Claiming...' : 'Claim Lead'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render category section
  const renderCategorySection = (category: SearchCategory) => {
    const items = categoryItems(category);
    if (items.length === 0) return null;

    const config = categoryConfig[category];
    const startIndex = CATEGORY_ORDER
      .slice(0, CATEGORY_ORDER.indexOf(category))
      .reduce((sum, c) => sum + categoryItems(c).length, 0);

    return (
      <div key={category}>
        <div className="px-6 py-3 bg-gray-100">
          <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
            {config.label}
          </span>
        </div>
        {category === 'coupon' && results?.coupon && (
          <div className="px-6 py-2 text-sm text-gray-500 border-b border-gray-100">
            {results.coupon.code}{results.coupon.agent_name ? ` — ${results.coupon.agent_name}` : ''} · latest orders, last {results.coupon.window_days} days
          </div>
        )}
        {items.map((item, idx) =>
          category === 'orders' || category === 'coupon'
            ? renderOrderRow(item, startIndex + idx)
            : renderResultItem(item, startIndex + idx)
        )}
      </div>
    );
  };

  return (
    <>
      {variant === 'bar' ? (
        /* Search bar — visible input-style trigger */
        <button
          onClick={() => setIsOpen(true)}
          className={cn(
            'flex items-center gap-2.5 h-8 px-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300 transition-colors text-left',
            'w-52',
            className
          )}
          title="Search (⌘K)"
        >
          <Search size={14} className="text-gray-400 shrink-0" />
          <span className="text-sm text-gray-400 flex-1">Name / Phone / Order / AWB / Coupon…</span>
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-white border border-gray-200 rounded">
            ⌘K
          </kbd>
        </button>
      ) : (
        /* Icon-only button (legacy / navbar use) */
        <button
          onClick={() => setIsOpen(true)}
          className={cn(
            'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200',
            'bg-white/10 hover:bg-white/20 text-white/70 hover:text-white',
            className
          )}
          title="Search (⌘K)"
        >
          <Search size={20} />
        </button>
      )}

      {/* Search Modal - rendered via portal to document.body */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 flex justify-center bg-black/50"
          onClick={handleClose}
          style={{ zIndex: 99999 }}
        >
          <div className="w-2/3 bg-white flex flex-col shadow-2xl mb-8 rounded-b-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
          {/* Search Header */}
          <div className="flex items-center gap-4 px-6 py-5 bg-gray-50">
            <Search size={24} className="text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={SEARCH_HINT}
              className="flex-1 text-xl bg-transparent border-none outline-none text-gray-900 placeholder-gray-400 font-medium tracking-wide"
              autoComplete="off"
            />
            {(isLoading || orderLoading) && <Loader2 size={24} className="text-brandColor animate-spin flex-shrink-0" />}
            {query && !isLoading && (
              <button
                onClick={() => { setQuery(''); setResults(null); inputRef.current?.focus(); }}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"
              >
                <X size={24} />
              </button>
            )}
            <button
              onClick={handleClose}
              className="ml-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>

          {/* Format helper — agents should not have to remember formats */}
          <div className="px-6 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400 flex gap-2 flex-wrap">
            {SEARCH_EXAMPLES.map((ex, i) => (
              <React.Fragment key={ex}>
                {i > 0 && <span>·</span>}
                <span>{ex}</span>
              </React.Fragment>
            ))}
          </div>

          {/* Results Area */}
          <div className="flex-1 overflow-y-auto">
            {orderCard ? renderOrderCard(orderCard) : (<>
            {/* Loading State */}
            {isLoading && !results && (
              <div className="flex items-center justify-center py-20 text-gray-500">
                <Loader2 size={32} className="animate-spin mr-3" />
                <span className="text-xl">Searching...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center py-20 text-red-500">
                <span className="text-xl">{error}</span>
              </div>
            )}

            {/* Results */}
            {results && !isLoading && (
              <>
                {results.total === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                    <Search size={64} className="mb-4 opacity-30" />
                    <p className="text-xl">No results found for "{query}"</p>
                    <p className="text-base text-gray-400 mt-2">Try a name, phone number, order ID, AWB or agent coupon</p>
                  </div>
                ) : (
                  <>
                    {CATEGORY_ORDER.map(c => renderCategorySection(c))}
                  </>
                )}
              </>
            )}

            {/* Empty Query Hint */}
            {!results && !isLoading && !error && (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <Search size={64} className="mb-4 opacity-30" />
                <p className="text-xl">Search customer or order</p>
                <p className="text-base text-gray-400 mt-2">{SEARCH_HINT}</p>
                {query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
                  <p className="text-sm text-gray-400 mt-4">Keep typing…</p>
                )}
              </div>
            )}
            </>)}
          </div>

          {/* Footer */}
          {results && results.total > 0 && !orderCard && (
            <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {results.total} result{results.total !== 1 ? 's' : ''} found
              </span>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-gray-200 rounded font-mono">↑</kbd>
                  <kbd className="px-2 py-1 bg-gray-200 rounded font-mono">↓</kbd>
                  <span className="ml-1">Navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-gray-200 rounded font-mono">↵</kbd>
                  <span className="ml-1">Select</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-gray-200 rounded font-mono">Esc</kbd>
                  <span className="ml-1">Close</span>
                </span>
              </div>
            </div>
          )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GlobalSearch;
